import { onDocumentReady } from '../utils/events';

onDocumentReady(() => {
  const interactiveMediaQuery = window.matchMedia('(min-width: 1024px)');
  const serviceGroups = document.querySelectorAll('.showcase__services');
  const previewTransitionDuration = 260;
  const fullTransitionDuration = 260;
  const widthTransitionDuration = 420;
  const heightTransitionDuration = 340;
  const serviceTimers = new WeakMap();

  serviceGroups.forEach((group) => {
    const serviceItems = Array.from(
      group.querySelectorAll('.showcase__service')
    ).map((service) => ({
      element: service,
      expandButton: service.querySelector('.showcase__service-expand'),
      full: service.querySelector('.showcase__full'),
      head: service.querySelector('.showcase__service-head'),
      preview: service.querySelector('.showcase__preview'),
    }));
    const services = serviceItems.map(({ element }) => element);
    const serviceData = new WeakMap(
      serviceItems.map((item) => [item.element, item])
    );
    const servicesSlider = group.closest('.showcase__services-slider');
    const sliderPrevButton = servicesSlider?.querySelector(
      '[data-showcase-slider-prev]'
    );
    const sliderNextButton = servicesSlider?.querySelector(
      '[data-showcase-slider-next]'
    );
    let sliderIndex = 0;
    let sliderStep = 0;
    let sliderOffset = 0;
    let refreshFrame = null;

    const canEnableSlider = () =>
      servicesSlider && interactiveMediaQuery.matches && services.length > 4;

    const getMaxSliderIndex = () => Math.max(services.length - 4, 0);

    const isServiceInCurrentSlide = (serviceIndex) =>
      serviceIndex >= sliderIndex && serviceIndex < sliderIndex + 4;

    const getSliderStep = () => {
      const firstService = services[0];

      if (!firstService) {
        return 0;
      }

      const groupStyles = window.getComputedStyle(group);
      const gap = parseFloat(groupStyles.columnGap || groupStyles.gap) || 0;

      return firstService.getBoundingClientRect().width + gap;
    };

    const setSliderOffset = (offset) => {
      sliderOffset = Math.max(offset, 0);
      servicesSlider?.style.setProperty(
        '--showcase-slider-offset',
        `${-sliderOffset}px`
      );
    };

    const keepServiceInViewport = (service) => {
      const viewportInset = 20;
      const viewportWidth = document.documentElement.clientWidth;
      const serviceRect = service.getBoundingClientRect();
      let adjustedOffset = sliderOffset;

      if (serviceRect.right > viewportWidth - viewportInset) {
        adjustedOffset += serviceRect.right - viewportWidth + viewportInset;
      } else if (serviceRect.left < viewportInset) {
        adjustedOffset -= viewportInset - serviceRect.left;
      }

      setSliderOffset(adjustedOffset);
    };

    const updateSlider = () => {
      if (!servicesSlider || !sliderPrevButton || !sliderNextButton) {
        return;
      }

      const isEnabled = canEnableSlider();
      const maxIndex = getMaxSliderIndex();
      const isSliderOpen = servicesSlider.classList.contains('is-slider-open');

      sliderIndex = Math.min(sliderIndex, maxIndex);
      servicesSlider.classList.toggle('is-slider-enabled', isEnabled);

      if (!isEnabled) {
        sliderIndex = 0;
      }

      if (!isSliderOpen) {
        sliderStep = getSliderStep();
        const maxSliderOffset = Math.max(
          group.scrollWidth - group.clientWidth,
          0
        );
        const nextSliderOffset = Math.min(
          sliderIndex * sliderStep,
          maxSliderOffset
        );

        setSliderOffset(nextSliderOffset);
      }

      sliderPrevButton.disabled = !isEnabled || sliderIndex === 0;
      sliderNextButton.disabled = !isEnabled || sliderIndex >= maxIndex;

      services.forEach((service, serviceIndex) => {
        const isOutsideCurrentSlide =
          isEnabled && !isServiceInCurrentSlide(serviceIndex);

        service.classList.toggle(
          'is-slide-inactive',
          isOutsideCurrentSlide && !isSliderOpen
        );
        service.classList.toggle(
          'is-slide-outside',
          isOutsideCurrentSlide && isSliderOpen
        );
        service.classList.toggle(
          'is-slide-visible-last',
          isEnabled && serviceIndex === sliderIndex + 3
        );
      });
    };

    const moveSlider = (direction) => {
      sliderIndex = Math.min(
        Math.max(sliderIndex + direction, 0),
        getMaxSliderIndex()
      );
      updateSlider();
    };

    const moveSliderAfterClose = (direction) => {
      if (hasActiveService()) {
        setActiveService(null, () => {
          moveSlider(direction);
        });
        return;
      }

      moveSlider(direction);
    };

    sliderPrevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveSliderAfterClose(-1);
    });

    sliderNextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveSliderAfterClose(1);
    });

    const clearServiceTimers = (service) => {
      const timers = serviceTimers.get(service) || [];

      timers.forEach((timer) => window.clearTimeout(timer));
      serviceTimers.set(service, []);
    };

    const addServiceTimer = (service, callback, delay) => {
      const timer = window.setTimeout(callback, delay);
      const timers = serviceTimers.get(service) || [];

      timers.push(timer);
      serviceTimers.set(service, timers);
    };

    const setServiceHeight = (service, height) => {
      service.style.height = `${Math.ceil(height)}px`;
    };

    const clearServiceHeight = (service) => {
      service.style.height = '';
    };

    const lockServiceHeights = () => {
      const serviceHeights = services.map(
        (service) => service.getBoundingClientRect().height
      );

      services.forEach((service, index) => {
        setServiceHeight(service, serviceHeights[index]);
      });
    };

    const hasActiveService = () =>
      services.some((service) => service.classList.contains('is-active'));

    const getVerticalSpacing = (element, properties) => {
      if (!element) {
        return 0;
      }

      const styles = window.getComputedStyle(element);

      return properties.reduce(
        (total, property) => total + (parseFloat(styles[property]) || 0),
        0
      );
    };

    const getServiceHeadHeight = (service) => {
      const serviceHead = serviceData.get(service)?.head;

      return serviceHead
        ? serviceHead.getBoundingClientRect().height +
            getVerticalSpacing(serviceHead, ['marginTop', 'marginBottom'])
        : 0;
    };

    const getServicePaddingHeight = (service) =>
      getVerticalSpacing(service, ['paddingTop', 'paddingBottom']);

    const getElementOuterHeight = (element) =>
      element
        ? element.getBoundingClientRect().height +
          getVerticalSpacing(element, ['marginTop', 'marginBottom'])
        : 0;

    const getFullContentHeight = (serviceFull) => {
      if (!serviceFull) {
        return 0;
      }

      return Array.from(serviceFull.children).reduce((total, child) => {
        const childStyles = window.getComputedStyle(child);
        const childHeight =
          parseFloat(childStyles.flexGrow) > 0
            ? Array.from(child.children).reduce(
                (childrenTotal, grandchild) =>
                  childrenTotal + getElementOuterHeight(grandchild),
                getVerticalSpacing(child, ['paddingTop', 'paddingBottom'])
              )
            : child.getBoundingClientRect().height;

        return (
          total +
          childHeight +
          getVerticalSpacing(child, ['marginTop', 'marginBottom'])
        );
      }, getVerticalSpacing(serviceFull, ['paddingTop', 'paddingBottom']));
    };

    const getActiveServiceHeight = (service) => {
      const serviceFull = serviceData.get(service)?.full;
      const serviceFullHeight =
        getFullContentHeight(serviceFull) +
        getVerticalSpacing(serviceFull, ['marginTop', 'marginBottom']);

      return (
        getServicePaddingHeight(service) +
        getServiceHeadHeight(service) +
        serviceFullHeight
      );
    };

    const getClosedServiceHeight = (service) => {
      const servicePreview = serviceData.get(service)?.preview;
      const servicePreviewHeight = servicePreview
        ? servicePreview.getBoundingClientRect().height +
          getVerticalSpacing(servicePreview, ['marginTop', 'marginBottom'])
        : 0;

      return (
        getServicePaddingHeight(service) +
        getServiceHeadHeight(service) +
        servicePreviewHeight
      );
    };

    const setClosedServiceHeights = () => {
      const maxServiceHeight = Math.max(
        ...services.map((service) => getClosedServiceHeight(service))
      );

      services.forEach((service) => {
        setServiceHeight(service, maxServiceHeight);
      });
    };

    const scheduleRefresh = () => {
      if (refreshFrame) {
        window.cancelAnimationFrame(refreshFrame);
      }

      refreshFrame = window.requestAnimationFrame(() => {
        refreshFrame = null;
        setClosedServiceHeights();
        updateSlider();
      });
    };

    updateSlider();
    window.addEventListener('load', () => {
      setClosedServiceHeights();
      updateSlider();
    });
    window.addEventListener('resize', scheduleRefresh);
    interactiveMediaQuery.addEventListener('change', scheduleRefresh);

    const closeService = (service, afterClose) => {
      clearServiceTimers(service);
      setServiceHeight(service, service.getBoundingClientRect().height);
      service.classList.remove('is-content-visible', 'is-height-adjusting');

      addServiceTimer(
        service,
        () => {
          service.classList.remove('is-active');
          serviceData
            .get(service)
            ?.expandButton?.setAttribute('aria-expanded', 'false');

          if (typeof afterClose === 'function') {
            afterClose();
          }
        },
        fullTransitionDuration
      );
    };

    const showPreviewsAfterWidthTransition = (afterShow) => {
      const timerOwner = services[0];

      if (!timerOwner) {
        if (typeof afterShow === 'function') {
          afterShow();
        }
        return;
      }

      addServiceTimer(
        timerOwner,
        () => {
          group.classList.remove('is-preview-out-of-flow');

          window.requestAnimationFrame(() => {
            setClosedServiceHeights();

            addServiceTimer(
              timerOwner,
              () => {
                group.classList.remove('is-preview-hidden');
                servicesSlider?.classList.remove('is-slider-open');
                updateSlider();

                if (typeof afterShow === 'function') {
                  afterShow();
                }
              },
              heightTransitionDuration
            );
          });
        },
        widthTransitionDuration
      );
    };

    const openService = (service) => {
      const isSwitchingActiveService = hasActiveService();
      const shouldLockClosedServices = !isSwitchingActiveService;

      servicesSlider?.classList.add('is-slider-open');
      updateSlider();

      if (shouldLockClosedServices) {
        lockServiceHeights();
      } else {
        setServiceHeight(service, service.getBoundingClientRect().height);
      }

      service.classList.remove('is-content-visible');
      service.classList.add('is-height-adjusting');
      group.classList.add('is-preview-hidden');

      if (isSwitchingActiveService) {
        group.classList.add('is-preview-out-of-flow');
      } else {
        group.classList.remove('is-preview-out-of-flow');
      }

      window.requestAnimationFrame(() => {
        addServiceTimer(
          service,
          () => {
            group.classList.add('is-preview-out-of-flow');

            services.forEach((otherService) => {
              if (otherService === service) {
                return;
              }

              otherService.classList.remove('is-active', 'is-content-visible');
              otherService.classList.remove('is-height-adjusting');
              serviceData
                .get(otherService)
                ?.expandButton?.setAttribute('aria-expanded', 'false');
              clearServiceHeight(otherService);
            });

            group.classList.add('has-active');
            service.classList.add('is-active');
            serviceData
              .get(service)
              ?.expandButton?.setAttribute('aria-expanded', 'true');

            addServiceTimer(
              service,
              () => {
                keepServiceInViewport(service);
                setServiceHeight(service, getActiveServiceHeight(service));

                addServiceTimer(
                  service,
                  () => {
                    service.classList.remove('is-height-adjusting');
                    service.classList.add('is-content-visible');

                    window.requestAnimationFrame(() => {
                      clearServiceHeight(service);
                    });
                  },
                  heightTransitionDuration
                );
              },
              widthTransitionDuration
            );
          },
          isSwitchingActiveService ? 0 : previewTransitionDuration
        );
      });
    };

    const setActiveService = (activeService, afterClose) => {
      if (activeService) {
        const currentActiveService = services.find(
          (service) =>
            service !== activeService && service.classList.contains('is-active')
        );

        services.forEach((service) => {
          clearServiceTimers(service);
          service.classList.remove('is-content-visible', 'is-height-adjusting');
        });

        if (currentActiveService) {
          addServiceTimer(
            currentActiveService,
            () => {
              openService(activeService);
            },
            fullTransitionDuration
          );
          return;
        }

        openService(activeService);
        return;
      }

      lockServiceHeights();

      services.forEach((service) => {
        if (service.classList.contains('is-active')) {
          closeService(service, () => {
            group.classList.remove('has-active');
            showPreviewsAfterWidthTransition(afterClose);
          });
        } else {
          clearServiceTimers(service);
          service.classList.remove('is-content-visible', 'is-height-adjusting');
          serviceData
            .get(service)
            ?.expandButton?.setAttribute('aria-expanded', 'false');
        }
      });
    };

    serviceItems.forEach(({ element: service, expandButton }) => {
      service.addEventListener('click', (event) => {
        if (!interactiveMediaQuery.matches) {
          return;
        }

        if (service.classList.contains('is-slide-inactive')) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (event.target.closest('a')) {
          return;
        }

        const isActive = service.classList.contains('is-active');
        setActiveService(isActive ? null : service);
      });

      expandButton?.addEventListener('click', (event) => {
        if (!interactiveMediaQuery.matches) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const isActive = service.classList.contains('is-active');
        setActiveService(isActive ? null : service);
      });
    });
  });
});
